import { hierarchy, treemap as d3Treemap, HierarchyRectangularNode } from "d3-hierarchy";
import { select } from "d3-selection";
import { scaleOrdinal } from "d3-scale";
import { Category } from "../../../databinding/Categorize";
import { Panel } from "../../panel/Panel";
import { Label } from "../../label/Label";

export interface CategoryTreemapOptions {
    width?: number;
    height?: number;
}

export class CategoryTreemap {
    private defaultOptions: CategoryTreemapOptions = {
        width: 850,
        height: 850
    };

    constructor(private options: CategoryTreemapOptions = {}) {
        this.options = { ...this.defaultOptions, ...options };
    }

    render(
        container: HTMLElement,
        categories: Category[],
        rootCategories: Category[],
        containerPanel: Panel,
        categoryFieldLabel: Label | undefined,
        formatTooltip: (cat: Category) => string,
        convertToHierarchy: (cats: Category[]) => any,
        updateFieldLabel: (cats: Category[]) => string
    ): void {
        const { width, height } = this.options;

        // Update the category field label
        if (categoryFieldLabel) {
            categoryFieldLabel.text = updateFieldLabel(categories);
        }

        // Convert categories to D3 hierarchy format
        const hierarchyData = convertToHierarchy(categories);

        // Create D3 hierarchy
        const root = hierarchy<any>(hierarchyData)
            .sum((d: any) => d.value || 0)
            .sort((a, b) => (b.value || 0) - (a.value || 0));

        // Create treemap layout
        const treemapLayout = d3Treemap<any>()
            .size([width, height])
            .padding(2)
            .round(true);

        treemapLayout(root);

        // Create SVG
        const svg = select(container)
            .append("svg")
            .attr("width", width)
            .attr("height", height)
            .style("font", "12px sans-serif")
            .style("background", "#fafafa");

        // Create color scale
        const color = scaleOrdinal<string>()
            .domain(["0", "1", "2", "3", "4", "5"])
            .range(["#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd", "#8c564b"]);

        // Create cells
        const cell = svg.selectAll<SVGGElement, HierarchyRectangularNode<any>>("g")
            .data(root.descendants().filter((d: any) => d.depth > 0))
            .join("g")
            .attr("transform", (d: any) => `translate(${d.x0},${d.y0})`);

        // Add rectangles
        cell.append("rect")
            .attr("width", (d: any) => d.x1 - d.x0)
            .attr("height", (d: any) => d.y1 - d.y0)
            .attr("fill", (d: HierarchyRectangularNode<any>) => color(d.depth.toString()))
            .attr("stroke", "#fff")
            .attr("stroke-width", 2)
            .style("cursor", (d: any) => {
                const category = d.data.originalCategory;
                return (category && category.subcategories && category.subcategories.length > 0) ? "pointer" : "default";
            })
            .style("fill-opacity", 0.8)
            .on("mouseenter", function() {
                select(this).style("fill-opacity", 1);
            })
            .on("mouseleave", function() {
                select(this).style("fill-opacity", 0.8);
            })
            .on("click", (event: MouseEvent, d: any) => {
                event.stopPropagation();
                const clickedCategory = d.data.originalCategory;
                if (clickedCategory && clickedCategory.subcategories && clickedCategory.subcategories.length > 0) {
                    container.innerHTML = "";
                    this.render(container, clickedCategory.subcategories, rootCategories, containerPanel, categoryFieldLabel, formatTooltip, convertToHierarchy, updateFieldLabel);
                }
            });

        // Add tooltips
        cell.append("title")
            .text((d: any) => {
                let tooltip = "";

                if (d.data.originalCategory) {
                    tooltip = formatTooltip(d.data.originalCategory);
                } else {
                    tooltip = `${d.data.name}\n`;
                }

                tooltip += `Items: ${d.data.itemCount || d.value || 0}`;
                return tooltip;
            });

        // Add text labels
        cell.append("text")
            .attr("x", 4)
            .attr("y", 14)
            .style("fill", "#fff")
            .style("font-size", "11px")
            .style("font-weight", "bold")
            .style("pointer-events", "none")
            .style("text-shadow", "0 1px 2px rgba(0,0,0,0.6)")
            .text((d: any) => {
                const width = d.x1 - d.x0;
                const height = d.y1 - d.y0;
                // Only show text if cell is large enough
                if (width < 30 || height < 20) return "";
                const name = d.data.name || "";
                const maxLength = Math.floor(width / 7);
                return name.length > maxLength ? name.substring(0, maxLength - 3) + "..." : name;
            });

        // Add count labels
        cell.append("text")
            .attr("x", 4)
            .attr("y", 28)
            .style("fill", "#fff")
            .style("font-size", "10px")
            .style("pointer-events", "none")
            .style("text-shadow", "0 1px 2px rgba(0,0,0,0.6)")
            .text((d: any) => {
                const width = d.x1 - d.x0;
                const height = d.y1 - d.y0;
                // Only show count if cell is large enough
                if (width < 40 || height < 35) return "";
                return `(${d.data.itemCount || d.value || 0})`;
            });

        // Add reset button if not at root
        if (categories !== rootCategories) {
            const resetButton = svg.append("g")
                .attr("transform", "translate(10,10)")
                .style("cursor", "pointer")
                .on("click", () => {
                    container.innerHTML = "";
                    this.render(container, rootCategories, rootCategories, containerPanel, categoryFieldLabel, formatTooltip, convertToHierarchy, updateFieldLabel);
                });

            resetButton.append("rect")
                .attr("x", 0)
                .attr("y", 0)
                .attr("width", 60)
                .attr("height", 20)
                .attr("fill", "#fff")
                .attr("stroke", "#999")
                .attr("stroke-width", 1)
                .attr("rx", 3);

            resetButton.append("text")
                .attr("x", 30)
                .attr("y", 10)
                .attr("dy", "0.35em")
                .attr("text-anchor", "middle")
                .style("font-size", "11px")
                .style("fill", "#333")
                .style("pointer-events", "none")
                .text("Reset");
        }
    }
}
